/**
 * SP-3 — the three data-gate answers collected before a first plan is authored.
 *
 * The server data gate (`api/lib/planning/dataGate.js`) blocks a plan authored
 * from ASSUMED mileage / experience / injury status. Onboarding never collected
 * these, so a fresh athlete failed all three the moment onboarding completed and
 * the engine would default to DEFAULT_WEEKLY_MINUTES. SP-3 collects them (option
 * A, ratified) and gates auto-draft on `missingTrainingData(ctx)` being empty.
 *
 * This module is the CLIENT mirror of the gate's field semantics + the exact
 * copy/options the collection beat renders. Pure — no React, no I/O — so it is
 * unit-tested against the server gate's key lists (the two must never diverge:
 * the server re-checks the SAME `missingTrainingData` in the materialize path).
 *
 * Mileage storage (locked constraint): the engine consumes the CONSERVATIVE
 * (lower) band edge via the canonical `weekly_mileage` key
 * (`weekMaterializer.js` reads `ctx['Weekly mileage'] || ctx.weekly_mileage`) —
 * never above what the athlete stated. The band id + an optional exact value are
 * stored alongside.
 */

// ── Data-gate mirror (MUST match api/lib/planning/dataGate.js) ────────────────

/** Mirrors the gate's MISSING_VALUES (mileage/experience). */
const MISSING_VALUES = ['not specified', 'unknown', 'n/a'];
/** Mirrors the gate's MISSING_INJURY_VALUES ("none"/"no injuries" is present). */
const MISSING_INJURY_VALUES = ['not specified', 'unknown'];

const MILEAGE_KEYS = [
  'Current weekly distance', 'Current weekly volume', 'Weekly distance',
  'Current weekly km', 'Weekly mileage', 'weekly_mileage',
];
const EXPERIENCE_KEYS = [
  'Running experience', 'Experience', 'Training age', 'Years running', 'experience',
];
const INJURY_KEYS = ['Injuries', 'Current injuries', 'Injury history', 'injuries'];

function hasValue(
  ctx: Record<string, unknown>,
  keys: string[],
  rejected: string[],
): boolean {
  return keys.some((k) => {
    const v = ctx[k];
    if (v === undefined || v === null || String(v).trim() === '') return false;
    return !rejected.includes(String(v).toLowerCase().trim());
  });
}

/** The three data-gate item ids, in the gate's stable order. */
export type MissingItem =
  | 'current weekly mileage'
  | 'running experience'
  | 'injury status';

/**
 * The safety-critical data points still missing from the athlete's context.
 * Empty array == the engine knows enough to author a plan safely. EXACT client
 * mirror of `dataGate.missingTrainingData` (same keys, same order, same "none"
 * semantics for injuries).
 */
export function missingTrainingData(
  context: Record<string, unknown> | null | undefined,
): MissingItem[] {
  const ctx = context && typeof context === 'object' ? context : {};
  const missing: MissingItem[] = [];
  if (!hasValue(ctx, MILEAGE_KEYS, MISSING_VALUES)) missing.push('current weekly mileage');
  if (!hasValue(ctx, EXPERIENCE_KEYS, MISSING_VALUES)) missing.push('running experience');
  if (!hasValue(ctx, INJURY_KEYS, MISSING_INJURY_VALUES)) missing.push('injury status');
  return missing;
}

// ── Mileage bands ─────────────────────────────────────────────────────────────

export type MileageBandId = 'lt10' | '10_20' | '20_30' | '30_plus';

export interface MileageBand {
  id: MileageBandId;
  label: string;
  sub: string;
  /** The conservative engine-facing miles for this band (its lower edge). */
  lowerMiles: number;
  /** Upper bound (exclusive) in miles; Infinity for the open top band. */
  ceilMiles: number;
}

/** Ordered low→high; the collection beat renders these as OptionRows. */
export const MILEAGE_BANDS: readonly MileageBand[] = [
  { id: 'lt10', label: 'Under 10 miles a week', sub: 'Or just getting started', lowerMiles: 5, ceilMiles: 10 },
  { id: '10_20', label: '10 to 20 miles a week', sub: 'A few runs most weeks', lowerMiles: 10, ceilMiles: 20 },
  { id: '20_30', label: '20 to 30 miles a week', sub: 'A consistent weekly rhythm', lowerMiles: 20, ceilMiles: 30 },
  { id: '30_plus', label: '30+ miles a week', sub: 'A high-volume base', lowerMiles: 30, ceilMiles: Infinity },
];

export function bandById(id: MileageBandId): MileageBand {
  return MILEAGE_BANDS.find((b) => b.id === id) ?? MILEAGE_BANDS[0];
}

/** The band a given (miles) value falls into — for deriving the band from an
 *  exact entry. Boundaries follow the labels: 10 → 10 to 20, 20 → 20 to 30,
 *  30 → 30+. */
export function bandFromMiles(miles: number): MileageBandId {
  for (const b of MILEAGE_BANDS) {
    if (miles < b.ceilMiles) return b.id;
  }
  return '30_plus';
}

/** Miles per km (an exact km entry is converted to the engine's mile unit). */
const MILES_PER_KM = 0.621371;

export type DistanceUnit = 'mi' | 'km';

/** Convert an exact entry to miles (rounded to 1 dp), the engine's unit. */
export function exactToMiles(value: number, unit: DistanceUnit): number {
  const mi = unit === 'km' ? value * MILES_PER_KM : value;
  return Math.round(mi * 10) / 10;
}

// ── B457 — the bands in the athlete's own unit ───────────────────────────────
//
// The band IDS and the stored miles never change (`weekly_mileage` is the band's
// lower edge in miles, the engine's unit). Only the words on screen follow the
// unit the athlete chose, and a km figure is the rounded equivalent of the mile
// edge: 10 mi reads 16 km, 20 mi 32 km, 30 mi 48 km.

/** Whole km for a mile edge (rounded, never invented precision). */
export function milesToKmRounded(miles: number): number {
  return Math.round(miles / MILES_PER_KM);
}

/** A band's lower edge in the chosen unit, for the "plan starts from" line. */
export function bandLowerIn(band: MileageBand, unit: DistanceUnit): number {
  return unit === 'km' ? milesToKmRounded(band.lowerMiles) : band.lowerMiles;
}

/** The band's row label in the chosen unit. Miles is the shipped label verbatim. */
export function bandLabelIn(band: MileageBand, unit: DistanceUnit): string {
  if (unit !== 'km') return band.label;
  const i = MILEAGE_BANDS.findIndex((b) => b.id === band.id);
  const hi = Number.isFinite(band.ceilMiles) ? milesToKmRounded(band.ceilMiles) : null;
  const lo = i > 0 ? milesToKmRounded(MILEAGE_BANDS[i - 1].ceilMiles) : 0;
  if (i <= 0) return `Under ${hi} km a week`;
  if (hi === null) return `${lo}+ km a week`;
  return `${lo} to ${hi} km a week`;
}

// ── B441 — the exact weekly distance, one rule for every path ────────────────
//
// Rulings 2026-10-21 item 12: distances are digits with at most one decimal
// point within a per-unit range. The ceiling is the wizard payload's own 0 to
// 250 miles guard (`buildOnboardingPayload`), which used to drop a bigger
// number silently while the first-look path stored it. The range per unit is
// the same limit in that unit, rounded down to a whole number.

/** The most miles a week the payload keeps; also the first-look merge's guard. */
export const MAX_WEEKLY_MILES = 250;
/** The most km a week that still converts to at most `MAX_WEEKLY_MILES`. */
export const MAX_WEEKLY_KM = Math.floor(MAX_WEEKLY_MILES / MILES_PER_KM);
/** The smallest exact entry (in the unit typed). Below it the bands fit better. */
export const MIN_WEEKLY_EXACT = 1;

const WEEKLY_DECIMAL_RE = /^(\d+\.?\d*|\.\d+)$/;

/**
 * Ruling 12 (2026-10-22): a comma followed by EXACTLY three digits ("1,000") reads
 * as a thousands separator, not a decimal comma, so the weekly box refuses it. It
 * used to read as 1 mile ("1.000"). "18,5" and "18,55" are still decimals.
 */
const THOUSANDS_LOOKALIKE_RE = /,\d{3}$/;

/**
 * Harvest fix round, UX M5 — an iPhone in es-CO, es-ES, pt-BR or fr shows a
 * comma and no point on the decimal pad, so "65,5" and "18,5" were refused (a
 * regression from the old number boxes, which the browser localised). ONE comma
 * with no point beside it stands for the decimal point; anything else (two
 * commas, a comma next to a point) is left as typed and fails the digit test.
 * Used by the weight box check and the payload parse (`usePersistOnboarding`)
 * and by the weekly distance box, so the box and the wire read a number alike
 * and the wire always carries a point.
 */
export function decimalPointText(raw: string | null | undefined): string {
  const text = (raw ?? '').trim();
  return /^[^.,]*,[^.,]*$/.test(text) ? text.replace(',', '.') : text;
}

/** The one-sentence refusal for an exact weekly distance, per unit. */
export const WEEKLY_DISTANCE_SENTENCE: Record<DistanceUnit, string> = {
  mi: `Use digits from ${MIN_WEEKLY_EXACT} to ${MAX_WEEKLY_MILES} miles, with at most one decimal point.`,
  km: `Use digits from ${MIN_WEEKLY_EXACT} to ${MAX_WEEKLY_KM} km, with at most one decimal point.`,
};

/**
 * The first-look merge's own guard (B441). The beat already refuses a number
 * outside the range, but the merge is the last door before the write, so it
 * applies the wizard payload's 0 to 250 miles test to the patch it is handed
 * and says why when a value is outside it. `null` means the patch may merge.
 */
export function weeklyMilesPatchProblem(patch: Record<string, unknown>): string | null {
  for (const key of ['weekly_mileage', 'weekly_mileage_exact'] as const) {
    if (!(key in patch)) continue;
    const v = patch[key];
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > MAX_WEEKLY_MILES) {
      return WEEKLY_DISTANCE_SENTENCE.mi;
    }
  }
  return null;
}

/**
 * Check what was typed in the exact weekly distance box. `empty` is true for a
 * blank box (nothing to refuse yet, nothing to save). A good entry carries the
 * number as typed and its value in miles, the engine's unit.
 */
export function checkExactWeekly(
  raw: string,
  unit: DistanceUnit,
):
  | { ok: true; value: number; miles: number }
  | { ok: false; empty: boolean; error: string } {
  const text = decimalPointText(raw);
  const error = WEEKLY_DISTANCE_SENTENCE[unit];
  if (text === '') return { ok: false, empty: true, error };
  if (THOUSANDS_LOOKALIKE_RE.test((raw ?? '').trim())) return { ok: false, empty: false, error };
  if (!WEEKLY_DECIMAL_RE.test(text)) return { ok: false, empty: false, error };
  const value = Number(text);
  const max = unit === 'km' ? MAX_WEEKLY_KM : MAX_WEEKLY_MILES;
  if (!(value >= MIN_WEEKLY_EXACT && value <= max)) return { ok: false, empty: false, error };
  return { ok: true, value, miles: exactToMiles(value, unit) };
}

// ── Experience ────────────────────────────────────────────────────────────────

export interface ExperienceOption {
  id: string;
  label: string;
}

export const EXPERIENCE_OPTIONS: readonly ExperienceOption[] = [
  { id: 'new', label: 'Just getting started' },
  { id: 'months', label: 'A few months' },
  { id: '1_2y', label: '1 to 2 years' },
  { id: '3plus', label: '3+ years' },
];

// ── Injuries ──────────────────────────────────────────────────────────────────

export type InjuryOptionId = 'none' | 'recovering' | 'managing';

export interface InjuryOption {
  id: InjuryOptionId;
  label: string;
  /** Recovering / managing offer an optional free-text detail line. */
  needsDetail: boolean;
}

export const INJURY_OPTIONS: readonly InjuryOption[] = [
  { id: 'none', label: 'No injuries', needsDetail: false },
  { id: 'recovering', label: 'Recovering from one', needsDetail: true },
  { id: 'managing', label: 'Managing something right now', needsDetail: true },
];

/**
 * The `injuries` free-text value stored for the engine's deterministic injury
 * parser (`api/lib/planning/injuryState.js` parseInjuryText). "No injuries"
 * resolves to healthy (NONE_RX); the recovering/managing labels resolve to a
 * conservative status, and any detail the athlete adds lets the parser pick a
 * site (and, for "managing", escalate to active on pain keywords).
 */
export function injuryText(id: InjuryOptionId, detail?: string | null): string {
  const opt = INJURY_OPTIONS.find((o) => o.id === id) ?? INJURY_OPTIONS[0];
  const d = (detail ?? '').trim();
  if (id === 'none' || !d) return opt.label;
  return `${opt.label}. ${d}`;
}

// ── B464 — the injury note's limit ───────────────────────────────────────────

/** The most characters the optional injury note takes (rulings 2026-10-22). */
export const INJURY_NOTE_MAX_CHARS = 500;
/** The sentence that names the limit. */
export const INJURY_NOTE_SENTENCE = `Keep the injury note to ${INJURY_NOTE_MAX_CHARS} characters or fewer.`;

/** `null` when the note fits, otherwise the sentence. Counted in characters a
 *  person sees (an emoji is one), on the trimmed text that would be saved. */
export function injuryNoteProblem(raw: string | null | undefined): string | null {
  const length = Array.from((raw ?? '').trim()).length;
  return length > INJURY_NOTE_MAX_CHARS ? INJURY_NOTE_SENTENCE : null;
}

// ── Persist payload ───────────────────────────────────────────────────────────

export interface TrainingDataInput {
  /** Omit a field when its question wasn't asked (that item was already
   *  present in context — the collection beat only asks the MISSING ones). */
  band?: MileageBandId | null;
  /** Exact miles when the athlete used the exact-number hatch (already in mi). */
  exactMiles?: number | null;
  experienceLabel?: string | null;
  injuriesText?: string | null;
}

/**
 * Build the canonical athlete-context patch the collection beat persists. Only
 * the fields present in `input` are emitted (an unasked, already-present item is
 * never overwritten). Keys are exactly what the data gate + the engine read:
 * `weekly_mileage` (the engine-facing conservative value = band lower edge, or
 * the exact miles when given), plus `weekly_mileage_band` /
 * `weekly_mileage_exact` metadata, `experience`, and `injuries`.
 */
export function buildTrainingDataPatch(input: TrainingDataInput): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (input.band) {
    const band = bandById(input.band);
    const hasExact = typeof input.exactMiles === 'number' && Number.isFinite(input.exactMiles) && input.exactMiles > 0;
    patch.weekly_mileage = hasExact ? (input.exactMiles as number) : band.lowerMiles;
    patch.weekly_mileage_band = input.band;
    if (hasExact) patch.weekly_mileage_exact = input.exactMiles;
  }
  if (input.experienceLabel) patch.experience = input.experienceLabel;
  if (input.injuriesText) patch.injuries = input.injuriesText;
  return patch;
}
